import { BRAND_DISPLAY_NAME } from '@streamlain/brand';

const DEVICE_ID_KEY = 'streamlain:push-device-id:v1';
const VAPID_VERSION_KEY = 'streamlain:push-vapid-version:v1';
const SERVICE_WORKER_PATH = `/mail-push-sw.js?brand=${encodeURIComponent(BRAND_DISPLAY_NAME)}`;

function deviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, created);
  return created;
}

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, char => char.charCodeAt(0));
}

export function shouldReplaceMailPushSubscription(
  registeredVersion: number | null,
  currentVersion: number
): boolean {
  return registeredVersion !== currentVersion;
}

export async function enableMailWebPush(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Web Push is not supported by this client');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted')
    throw new Error('Notification permission denied');
  const registration = await navigator.serviceWorker.register(
    SERVICE_WORKER_PATH,
    {
      scope: '/',
    }
  );
  const configResponse = await fetch(
    '/api/notification/push-devices/configuration',
    {
      credentials: 'include',
    }
  );
  if (!configResponse.ok) throw new Error('Push configuration unavailable');
  const config = (await configResponse.json()) as {
    publicKey: string;
    keyVersion: number;
  };
  let subscription = await registration.pushManager.getSubscription();
  const registeredVersion = Number(localStorage.getItem(VAPID_VERSION_KEY));
  if (
    subscription &&
    shouldReplaceMailPushSubscription(
      Number.isInteger(registeredVersion) ? registeredVersion : null,
      config.keyVersion
    )
  ) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey(config.publicKey),
  });
  const response = await fetch('/api/notification/push-devices', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      clientDeviceId: deviceId(),
      subscription: subscription.toJSON(),
    }),
  });
  if (!response.ok) throw new Error('Push device registration failed');
  localStorage.setItem(VAPID_VERSION_KEY, String(config.keyVersion));
  registration.active?.postMessage({ type: 'mail-push:retry' });
}

export async function disableMailWebPush(): Promise<void> {
  const id = localStorage.getItem(DEVICE_ID_KEY);
  if (id) {
    await fetch(`/api/notification/push-devices/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  }
  const registration = await navigator.serviceWorker?.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  await subscription?.unsubscribe();
}

export async function purgeMailPushClientState(): Promise<void> {
  await disableMailWebPush().catch(() => undefined);
  localStorage.removeItem(DEVICE_ID_KEY);
  localStorage.removeItem(VAPID_VERSION_KEY);
  const registration = await navigator.serviceWorker?.getRegistration('/');
  registration?.active?.postMessage({ type: 'mail-push:purge' });
}
